<template>
  <TableComp
    :headers="headers"
    :name="$t('global.tickets')"
    :status="tickets.status"
    :pagination="tickets.pagination"
    :sort="tickets.sort"
    :number-of-items="tickets.list.length"
    @update:list="updateList"
    :emptyBoldMessage="$t('global.no-tickets-bold-message')"
    emptyImage="tickets"
    :emptyMessage="$t('global.no-tickets-message')"
  >
    <template #desktop>
      <tr v-for="ticket in tickets.list" :key="ticket.id">
        <td>
          <div>
            <router-link :to="{ name: 'ticket-detail', params: { id: ticket.id } }">
              {{ ticket.mask }}
            </router-link>
          </div>
        </td>
        <td class="td-max-content">
          <div class="break-word">
            {{ truncate(ticket.subject, 65) }}
          </div>
        </td>
        <td>
          <div class="d-flex justify-content-center">
            {{ getTicketServiceIdentity(ticket) }}
          </div>
        </td>
        <td>
          <div class="d-flex justify-content-center">
            <Pill
              v-if="ticket.status"
              :content="ticket.status.identity"
              :variant="getTicketStatusColor(ticket)"
            />
          </div>
        </td>
        <td class="text-center">
          <div class="d-flex justify-content-center">
            {{ getTimeSinceLastReply(ticket) }}
          </div>
        </td>
        <td class="text-center">
          <div class="d-flex justify-content-center">
            <Pill
              v-if="ticket.priority"
              :content="ticket.priority.identity"
              :variant="getTicketPriorityColor(ticket)"
            />
          </div>
        </td>
        <td class="text-end actions">
          <div class="d-flex justify-content-end mr-4">
            <ThreeDotsMenu :items="menuItems(ticket)" />
          </div>
        </td>
      </tr>
    </template>
    <template #mobile>
      <TableMobileRow
        v-for="ticket in tickets.list"
        :key="ticket.id"
        :menu-items="menuItems(ticket)"
        :rows="mobileRows(ticket)"
      >
        <template #header>
          <div class="d-flex align-items-center">
            <div class="flex-fill">
              <router-link :to="{ name: 'ticket-detail', params: { id: ticket.id } }">
                {{ ticket.mask }}
              </router-link>
              - {{ truncate(ticket.subject, 40) }}
            </div>
            <div>
              <div class="d-flex justify-content-center">
                <ThreeDotsMenu :items="menuItems(ticket)" />
              </div>
            </div>
          </div>
        </template>

        <template #content>
          <!-- ticket service -->
          <div class="row">
            <div class="col">{{ $t("global.service") }}:</div>
            <div class="col">
              {{ getTicketServiceIdentity(ticket) }}
            </div>
          </div>
          <!-- ticket status -->
          <div class="row">
            <div class="col">{{ $t("global.status") }}:</div>
            <div class="col">
              <Pill
                v-if="ticket.status"
                :content="ticket.status.identity"
                :variant="getTicketStatusColor(ticket)"
              />
            </div>
          </div>

          <!-- ticket priority -->
          <div class="row">
            <div class="col">{{ $t("global.priority") }}:</div>
            <div class="col">
              <Pill
                v-if="ticket.priority"
                :content="ticket.priority.identity"
                :variant="getTicketPriorityColor(ticket)"
              />
            </div>
          </div>
        </template>
      </TableMobileRow>
    </template>
  </TableComp>
</template>

<script>
import { closeTicket, getTicketPriorityColor, getTicketStatusColor } from "@/utils/useTickets";
import { canWrite } from "@/utils/useAuth";
import NavLink from "@/classes/NavLink";
import Pill from "@/components/display/Pill";
import store from "@/store";
import TableComp from "@/components/display/table/TableComp";
import TableHeader from "@/classes/TableHeader";
import TableMobileRow from "@/components/display/table/TableMobileRow";
import ThreeDotsMenu from "@/components/display/ThreeDotsMenu";
import TicketQuickReply from "@/components/smart/modals/TicketQuickReply";
import { timeSince } from "leatherman-js/utils/formatters/time";
import { truncate } from "leatherman-js/utils/formatters/text";

export default {
  name: "TicketsTable",
  props: {
    tickets: Object,
  },
  components: {
    Pill,
    TableComp,
    TableMobileRow,
    ThreeDotsMenu,
  },
  setup(props, { root }) {
    const getTicketServiceIdentity = (ticket) => {
      return ticket.service?.id || "";
    };

    const getTimeSinceLastReply = (ticket) => {
      return ticket.recentPost?.date ? timeSince(ticket.recentPost.date) : "";
    };

    const headers = [
      new TableHeader(root.$t("global.ticket-id")),
      new TableHeader(root.$t("global.subject"), "justify-content-center", "subject"),
      new TableHeader(root.$t("global.service"), "justify-content-center"),
      new TableHeader(root.$t("global.status"), "justify-content-center", "status_id"),
      new TableHeader(root.$t("tickets.last-reply"), "justify-content-center"),
      new TableHeader(root.$t("global.priority"), "justify-content-center", "priority_id"),
      new TableHeader(),
    ];

    const menuItems = (ticket) => {
      const items = [
        new NavLink(
          root.$t("global.view"),
          null,
          { name: "ticket-detail", params: { id: ticket.id } },
          false,
          "default"
        ),
      ];

      if (canWrite("tickets")) {
        items.push(
          new NavLink(
            root.$t("tickets.quick-reply"),
            null,
            () => {
              store.dispatch("modals/open", {
                template: TicketQuickReply,
                data: { ticket },
              });
            },
            false,
            "default"
          )
        );
      }

      if (canWrite("tickets") && !ticket.isClosed && ticket.isCloseable) {
        items.push(
          new NavLink(root.$t("global.close"), null, () => closeTicket(ticket), false, "danger")
        );
      }
      return items;
    };

    const mobileRows = (ticket) => [
      { title: root.$t("global.status"), data: ticket.formattedStatus },
      { title: root.$t("global.due-date"), data: ticket.formattedDueDate },
      { title: root.$t("global.total"), data: ticket.due },
    ];

    const updateList = (event) => {
      const { pagination, sort } = event;
      store.dispatch("tickets/getList", {
        pageIndex: pagination?.index,
        pageSize: pagination?.size,
        sortProperty: sort?.property,
        sortDirection: sort?.direction,
      });
    };

    return {
      // getParticipants,
      getTicketPriorityColor,
      getTicketServiceIdentity,
      getTicketStatusColor,
      getTimeSinceLastReply,
      headers,
      menuItems,
      mobileRows,
      timeSince,
      truncate,
      updateList,
    };
  },
};
</script>

<style scoped>
p {
  word-break: break-word;
}
</style>
